import { useLazyQuery } from "@apollo/client";
import { IonItem, IonItemDivider, IonItemGroup, IonLabel, IonList } from "@ionic/react";
import { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { RichTextareaContext } from "@components/fields/RichTextareaProvider";
import { MessageTemplateMergeTagEnum } from "@typing/Enums";
import {
  GetMessageTemplatesDocument,
  GetMessageTemplatesQuery,
  GetMessageTemplatesQueryVariables,
  MessageTemplatesSortByEnum
} from "@typing/Generated";
import { IconName } from "@utils/iconUtils";
import { sortByTitle } from "@utils/modelUtils";

type MessageTemplate = NonNullable<
  NonNullable<NonNullable<GetMessageTemplatesQuery["messageTemplates"]["edges"]>[number]>["node"]
>;

type EnhancedMessageTemplate = MessageTemplate & {
  icon: IconName | null;
};

type Props = {
  onSelect: () => void;
};

const RichTextareaTemplatesSelect = ({ onSelect }: Props) => {
  const intl = useIntl();
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);
  const [messageTemplateCategoryMap, setMessageTemplateCategoryMap] = useState<Map<string, EnhancedMessageTemplate[]>>(
    new Map()
  );

  const { editor, journey, setOverwriteContent, setTemplateId, setWarning } = useContext(RichTextareaContext);

  const generateMessageTemplateCategoryMap = (templates: MessageTemplate[]) => {
    const map = new Map<string, EnhancedMessageTemplate[]>();

    sortByTitle(templates).forEach(template => {
      const key = template.messageTemplateCategory.title;
      const collection = map.get(key);
      const icon = journey?.usedMessageTemplateIds?.some(id => id === template.id) ? "history" : null;
      if (!collection) {
        map.set(key, [{ ...template, icon }]);
      } else {
        collection.push({ ...template, icon });
      }
    });
    const sortedMap = new Map([...map].sort((a, b) => String(a[0]).localeCompare(b[0])));
    return sortedMap;
  };

  const [getMessageTemplates, getMessageTemplatesQuery] = useLazyQuery<
    GetMessageTemplatesQuery,
    GetMessageTemplatesQueryVariables
  >(GetMessageTemplatesDocument);

  useEffect(() => {
    getMessageTemplates({
      variables: {
        sortBy: MessageTemplatesSortByEnum.TITLE
      }
    });
  }, [getMessageTemplates]);

  useEffect(() => {
    if (!getMessageTemplatesQuery.loading && getMessageTemplatesQuery.data?.messageTemplates.pageInfo.hasNextPage) {
      getMessageTemplatesQuery.fetchMore({
        variables: { cursor: getMessageTemplatesQuery.data?.messageTemplates.pageInfo.endCursor ?? "" }
      });
    }
  }, [getMessageTemplatesQuery]);

  useEffect(() => {
    if (
      !initialLoadComplete &&
      !getMessageTemplatesQuery.loading &&
      getMessageTemplatesQuery.data &&
      !getMessageTemplatesQuery.data.messageTemplates.pageInfo.hasNextPage
    ) {
      setInitialLoadComplete(true);
    }
  }, [getMessageTemplatesQuery, initialLoadComplete]);

  // after the data has loaded, run the query with an empty string to get the default options
  useEffect(() => {
    if (initialLoadComplete && getMessageTemplatesQuery.data && getMessageTemplatesQuery.data.messageTemplates.edges) {
      const loadedTemplates = getMessageTemplatesQuery.data.messageTemplates.edges
        .map(edge => edge?.node)
        .filter((x): x is MessageTemplate => !!x);
      const categoryMap = generateMessageTemplateCategoryMap(loadedTemplates);
      setMessageTemplateCategoryMap(categoryMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadComplete]);

  // the template id is used to associate a message with a template. if you clear the content of the
  // editor, we need to clear the template id too.
  useEffect(() => {
    if (editor?.getText() === "" && setTemplateId) {
      setTemplateId(null);
    }
  }, [editor, setTemplateId]);

  const replacementForTemplateMergeTag = (variable: MessageTemplateMergeTagEnum) => {
    switch (variable) {
      case MessageTemplateMergeTagEnum.COMPANY_NAME:
        return journey?.owner.employer?.name;
      case MessageTemplateMergeTagEnum.EXPERT_FIRST_NAME:
        return journey?.primaryExpert?.firstName;
      case MessageTemplateMergeTagEnum.OWNER_MEMBER_FIRST_NAME:
        return journey?.owner.firstName;
      default:
        return null;
    }
  };

  const handleSelect = (messageTemplate: EnhancedMessageTemplate) => {
    let content = messageTemplate.content;

    const warnings: string[] = [];

    Object.values(MessageTemplateMergeTagEnum).forEach(mergeTag => {
      const replacement = replacementForTemplateMergeTag(mergeTag);

      if (replacement) {
        content = content.replaceAll(mergeTag, replacement);
      } else if (content.includes(mergeTag)) {
        warnings.push(intl.formatMessage({ id: `pages.expert.sharableUrls.mergeTagWarnings.${mergeTag}` }));
      }
    });

    setOverwriteContent(content);
    setTemplateId && setTemplateId(messageTemplate.id);
    onSelect();

    if (warnings.length > 0) {
      setWarning(warnings.join(" "));
    }
  };

  return (
    <IonList className="ion-no-padding">
      {Array.from(messageTemplateCategoryMap).map(([category, messageTemplates]) => (
        <IonItemGroup key={category}>
          <IonItemDivider>
            <IonLabel>{category}</IonLabel>
          </IonItemDivider>
          {messageTemplates.map(item => (
            <IonItem button color="primary-50" key={item.id} lines="full" onClick={() => handleSelect(item)}>
              <IonLabel>{item.title}</IonLabel>
            </IonItem>
          ))}
        </IonItemGroup>
      ))}
    </IonList>
  );
};

export default RichTextareaTemplatesSelect;
